/**
 * Template Styles Configuration
 * 每個模板的元件樣式差異，用於 ProductList / ProductDetail / Checkout 動態套用
 */

const TEMPLATE_STYLES = {
  classic: {
    card: {
      borderRadius: 2,
      hoverTransform: 'translateY(-4px)',
      hoverShadow: '0 12px 24px rgba(0,0,0,0.12)',
      imageScale: 1.05,
    },
    image: {
      aspectRatio: '100%', // 1:1
      borderRadius: 2,
    },
    button: {
      variant: 'contained',
      borderRadius: 2,
      textTransform: 'none',
      addToCartText: '加入購物車',
      buyNowText: '立即購買',
    },
    badge: {
      format: (discount) => `${discount}% OFF`,
      borderRadius: 1,
      fontWeight: 'bold',
    },
    price: {
      fontFamily: 'inherit',
      fontWeight: 'bold',
    },
    chip: {
      borderRadius: undefined, // MUI default
    },
    special: {},
  },

  fashion: {
    card: {
      borderRadius: 0,
      hoverTransform: 'translateY(-8px)',
      hoverShadow: '0 20px 40px rgba(0,0,0,0.1)',
      imageScale: 1.1,
    },
    image: {
      aspectRatio: '133%', // 3:4
      borderRadius: 0,
    },
    button: {
      variant: 'outlined',
      borderRadius: 0,
      textTransform: 'uppercase',
      addToCartText: 'ADD TO BAG',
      buyNowText: 'BUY NOW',
      letterSpacing: '0.12em',
    },
    badge: {
      format: () => 'SALE',
      borderRadius: 0,
      fontWeight: 'bold',
      letterSpacing: '0.1em',
    },
    price: {
      fontFamily: 'inherit',
      fontWeight: 300,
      letterSpacing: '0.05em',
    },
    chip: {
      borderRadius: 0,
    },
    special: {
      uppercase: true,
      letterSpacing: '0.05em',
    },
  },

  handmade: {
    card: {
      borderRadius: 4,
      hoverTransform: 'translateY(-6px)',
      hoverShadow: '0 16px 32px rgba(139,111,71,0.15)',
      imageScale: 1.04,
    },
    image: {
      aspectRatio: '100%',
      borderRadius: 4,
    },
    button: {
      variant: 'contained',
      borderRadius: 50, // pill (was 3 in plan, pill feels more handmade-craft)
      textTransform: 'none',
      addToCartText: '加入購物車',
      buyNowText: '立即購買',
    },
    badge: {
      format: () => null, // handmade: no discount badge
      borderRadius: 2,
      fontWeight: 'bold',
    },
    price: {
      fontFamily: '"Noto Serif TC", serif',
      fontWeight: 'bold',
    },
    chip: {
      borderRadius: 50,
    },
    special: {
      serifHeaders: true,
      headerFontFamily: '"Noto Serif TC", serif',
    },
  },

  food: {
    card: {
      borderRadius: 4,
      hoverTransform: 'translateY(-6px)',
      hoverShadow: '0 16px 32px rgba(212,80,58,0.15)',
      imageScale: 1.06,
    },
    image: {
      aspectRatio: '100%',
      borderRadius: 4,
    },
    button: {
      variant: 'contained',
      borderRadius: 50, // pill
      textTransform: 'none',
      addToCartText: '立即購買',
      buyNowText: '馬上下單',
    },
    badge: {
      format: (discount) => `${discount}% OFF`,
      borderRadius: 50,
      fontWeight: 'bold',
    },
    price: {
      fontFamily: 'inherit',
      fontWeight: 'bold',
    },
    chip: {
      borderRadius: 50,
    },
    special: {},
  },

  tech: {
    card: {
      borderRadius: 2,
      hoverTransform: 'translateY(-6px)',
      hoverShadow: (primary) => `0 8px 32px ${primary}33, 0 0 12px ${primary}22`,
      imageScale: 1.05,
      glowBorder: true,
    },
    image: {
      aspectRatio: '100%',
      borderRadius: 2,
    },
    button: {
      variant: 'contained',
      borderRadius: 1.5,
      textTransform: 'none',
      addToCartText: '加入購物車',
      buyNowText: '立即購買',
    },
    badge: {
      format: (discount) => `-${discount}%`,
      borderRadius: 1,
      fontWeight: 'bold',
      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
    },
    price: {
      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
      fontWeight: 'bold',
    },
    chip: {
      borderRadius: 1,
    },
    special: {
      cornerBrackets: true,
      glowBorder: true,
      monoPrice: true,
    },
  },
};

/**
 * 取得模板樣式配置
 * @param {string} templateId - 模板 ID
 * @returns {object} 完整樣式配置
 */
export const getTemplateStyles = (templateId) => {
  return TEMPLATE_STYLES[templateId] || TEMPLATE_STYLES.classic;
};

/**
 * 取得商品卡片 hover sx 樣式
 * @param {string} templateId
 * @param {string} primaryColor - 模板主色
 */
export const getCardHoverSx = (templateId, primaryColor) => {
  const s = getTemplateStyles(templateId);
  const shadow = typeof s.card.hoverShadow === 'function'
    ? s.card.hoverShadow(primaryColor)
    : s.card.hoverShadow;

  const hoverSx = {
    transform: s.card.hoverTransform,
    boxShadow: shadow,
    borderColor: 'primary.main',
    '& .product-image': {
      transform: `scale(${s.card.imageScale})`,
    },
  };

  // Tech glow border
  if (s.card.glowBorder) {
    hoverSx.boxShadow = shadow;
  }

  return hoverSx;
};

/**
 * 產生折扣標籤文字
 * @param {string} templateId
 * @param {number} discount - 折扣百分比
 * @returns {string|null} 標籤文字，null 表示不顯示
 */
export const formatDiscountBadge = (templateId, discount) => {
  const s = getTemplateStyles(templateId);
  if (!s.badge.format) return `${discount}% OFF`;
  return s.badge.format(discount);
};

export default TEMPLATE_STYLES;
